import { MsalProvider } from '@azure/msal-react';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { ReactNode, useEffect, useState } from 'react';
import { msalConfig } from './authConfig';

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL before any other API calls (required for msal-browser v3+)
const msalInitPromise = msalInstance.initialize();

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider wraps the application with MSAL context.
 * Awaits MSAL initialization, then handles redirect promise and sets active account.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    msalInitPromise.then(() => {
      // Handle redirect responses after initialization
      return msalInstance.handleRedirectPromise();
    }).then((response) => {
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      }

      // Set default active account if one exists
      if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
      }

      setIsInitialized(true);
    });

    // Set active account on login success
    const callbackId = msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as { account: { homeAccountId: string } };
        const account = msalInstance.getAccountByHomeId(payload.account.homeAccountId);
        if (account) {
          msalInstance.setActiveAccount(account);
        }
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  if (!isInitialized) {
    return null; // Or a loading spinner
  }

  return (
    <MsalProvider instance={msalInstance}>
      {children}
    </MsalProvider>
  );
}
